import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { Button, Table } from 'reactstrap';
import { getRateWorkingPreview, applyDesignMixToRateWorking, getDieselRate } from '../services/ConcreteEstimatorServices';
import DesignMixSelector from './DesignMixSelector';
import { getAllTax } from '../services/TaxServices';
import { applyDesignMixToMaterials, mixRecipeSnapshot, uniqueGradesFromLines, RATE_WORKING_BASIS_M3 } from '../utils/concreteEstimatorDesignMix';
import {
    calculateRateTotals,
    computeTransportCharges,
    DEFAULT_AVG_KM_PER_LTR,
    DEFAULT_VEHICLE_CAPACITY_M3,
    findDefaultGst18Tax,
    formatCurrency,
    formatRatePerM3,
    formatRateWorkingUnit,
    formatTaxLabel,
    groupMaterials,
    num,
    taxPercentFromRecord,
} from '../utils/concreteEstimatorRateCalc';

const EMPTY_SHEET = {
    materials: [],
    company_overhead: 0,
    transport_charges: 0,
    diesel_rate: 0,
    vehicle_capacity: DEFAULT_VEHICLE_CAPACITY_M3,
    avg_km_per_ltr: DEFAULT_AVG_KM_PER_LTR,
    profit_mode: 'percent',
    profit_value: 0,
    tax_id: null,
    tax_name: 'GST @ 18%',
    tax_percent: 18,
    volume_m3: RATE_WORKING_BASIS_M3,
};

function ConcreteEstimatorRateWorking({
    cookies,
    plantId,
    customerId,
    consigneeId,
    lines = [],
    totals,
    distanceRoundTripKm,
    currentDieselRate = 0,
    grnDieselRate = 0,
    onDieselRateChange,
    editId,
    initialSheet,
    onChange,
}) {
    const [taxList, setTaxList] = useState([]);
    const [sheet, setSheet] = useState(EMPTY_SHEET);
    const [loading, setLoading] = useState(false);
    const [designMixSearch, setDesignMixSearch] = useState('');
    const [gradeFilter, setGradeFilter] = useState('');
    const [selectedMixId, setSelectedMixId] = useState(null);
    const [selectedMixRow, setSelectedMixRow] = useState(null);
    const userEditedRef = useRef(false);
    const initialLoadedRef = useRef(false);
    const sheetRef = useRef(sheet);
    sheetRef.current = sheet;

    useEffect(() => {
        getAllTax(cookies).then((res) => {
            setTaxList((res.tax_list || []).filter((t) => t.status));
        }).catch(() => {});
    }, [cookies]);

    const transportCharges = useMemo(
        () => computeTransportCharges(
            distanceRoundTripKm,
            num(sheet.diesel_rate),
            sheet.vehicle_capacity,
            sheet.avg_km_per_ltr,
        ),
        [distanceRoundTripKm, sheet.diesel_rate, sheet.vehicle_capacity, sheet.avg_km_per_ltr],
    );

    const computedSheet = useMemo(
        () => calculateRateTotals({
            ...sheet,
            transport_charges: transportCharges,
            distance_round_trip_km: distanceRoundTripKm,
            volume_m3: RATE_WORKING_BASIS_M3,
        }),
        [sheet, transportCharges, distanceRoundTripKm],
    );

    useEffect(() => {
        onChange?.(computedSheet);
    }, [computedSheet, onChange]);

    const applySheet = useCallback((next) => {
        setSheet(next);
    }, []);

    const loadPreview = useCallback((preserveUserRates = false) => {
        if (!plantId) return;
        setLoading(true);
        getRateWorkingPreview(cookies, {
            plant_id: plantId,
            cement_kg: totals.total_cement_kg,
            sand_kg: totals.total_sand_kg,
            aggregate_kg: totals.total_aggregate_kg,
            volume_m3: totals.total_volume_m3,
            distance_round_trip_km: distanceRoundTripKm || 0,
            estimator_id: editId || undefined,
        })
            .then((data) => {
                if (userEditedRef.current && !preserveUserRates) {
                    return;
                }
                if (preserveUserRates && sheetRef.current.materials?.length) {
                    const savedByProduct = {};
                    sheetRef.current.materials.forEach((row) => {
                        savedByProduct[row.product_id] = row;
                    });
                    data.materials = (data.materials || []).map((row) => ({
                        ...row,
                        rate: savedByProduct[row.product_id]?.rate ?? row.rate,
                        quantity: savedByProduct[row.product_id]?.quantity ?? row.quantity,
                    }));
                    data.company_overhead = sheetRef.current.company_overhead;
                    data.diesel_rate = sheetRef.current.diesel_rate ?? data.diesel_rate;
                    data.vehicle_capacity = sheetRef.current.vehicle_capacity ?? data.vehicle_capacity;
                    data.avg_km_per_ltr = sheetRef.current.avg_km_per_ltr ?? data.avg_km_per_ltr;
                    data.profit_mode = sheetRef.current.profit_mode;
                    data.profit_value = sheetRef.current.profit_value;
                    data.tax_id = sheetRef.current.tax_id;
                    data.tax_name = sheetRef.current.tax_name;
                    data.tax_percent = sheetRef.current.tax_percent;
                    data.selected_design_mix_id = sheetRef.current.selected_design_mix_id;
                    data.selected_design_mix_label = sheetRef.current.selected_design_mix_label;
                    data.selected_design_mix_recipe = sheetRef.current.selected_design_mix_recipe;
                }
                applySheet(data);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [
        applySheet,
        cookies,
        distanceRoundTripKm,
        editId,
        plantId,
        totals.total_aggregate_kg,
        totals.total_cement_kg,
        totals.total_sand_kg,
        totals.total_volume_m3,
    ]);

    useEffect(() => {
        if (initialSheet?.materials?.length && !initialLoadedRef.current) {
            initialLoadedRef.current = true;
            applySheet({
                ...initialSheet,
                diesel_rate: num(initialSheet.diesel_rate ?? initialSheet.current_diesel_rate ?? currentDieselRate),
                vehicle_capacity: num(initialSheet.vehicle_capacity, DEFAULT_VEHICLE_CAPACITY_M3),
                avg_km_per_ltr: num(initialSheet.avg_km_per_ltr, DEFAULT_AVG_KM_PER_LTR),
            });
            setSelectedMixId(initialSheet.selected_design_mix_id || null);
            if (initialSheet.selected_design_mix_recipe) {
                setSelectedMixRow(initialSheet.selected_design_mix_recipe);
                setGradeFilter(initialSheet.selected_design_mix_recipe.grade || '');
            } else if (initialSheet.selected_design_mix_label) {
                const gradeToken = String(initialSheet.selected_design_mix_label).split('·')[0]?.trim();
                if (gradeToken) setGradeFilter(gradeToken);
            }
            return;
        }
        if (!plantId || userEditedRef.current) return;
        loadPreview(false);
    }, [applySheet, initialSheet, loadPreview, plantId, currentDieselRate]);

    useEffect(() => {
        setSheet((prev) => ({
            ...prev,
            diesel_rate: currentDieselRate >= 0 ? currentDieselRate : num(prev.diesel_rate),
        }));
    }, [plantId, currentDieselRate]);

    useEffect(() => {
        if (!plantId || userEditedRef.current) return;
        if (initialSheet?.materials?.length && !initialLoadedRef.current) return;
        const timer = setTimeout(() => loadPreview(true), 400);
        return () => clearTimeout(timer);
    }, [
        initialSheet,
        loadPreview,
        plantId,
        totals.total_cement_kg,
        totals.total_sand_kg,
        totals.total_aggregate_kg,
        totals.total_volume_m3,
    ]);

    const updateMaterial = (productId, field, value) => {
        userEditedRef.current = true;
        setSheet((prev) => ({
            ...prev,
            materials: (prev.materials || []).map((row) => (
                row.product_id === productId ? { ...row, [field]: value } : row
            )),
        }));
    };

    const updateField = (field, value) => {
        userEditedRef.current = true;
        setSheet((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'diesel_rate') {
                onDieselRateChange?.(value);
            }
            return next;
        });
    };

    const fetchGrnDieselRate = () => {
        if (!plantId) return;
        getDieselRate(cookies, plantId)
            .then((res) => {
                const grnRate = Number(res.grn_diesel_rate) || 0;
                userEditedRef.current = true;
                setSheet((prev) => ({ ...prev, diesel_rate: grnRate }));
                onDieselRateChange?.(grnRate);
            })
            .catch(() => {});
    };

    const onTaxChange = (e) => {
        const taxId = e.target.value ? Number(e.target.value) : null;
        const tax = taxList.find((t) => t.id === taxId);
        userEditedRef.current = true;
        setSheet((prev) => ({
            ...prev,
            tax_id: taxId,
            tax_name: tax?.name || prev.tax_name,
            tax_percent: taxPercentFromRecord(tax || findDefaultGst18Tax(taxList)),
        }));
    };

    const resetGrnRates = () => {
        setSheet((prev) => ({
            ...prev,
            materials: (prev.materials || []).map((row) => ({
                ...row,
                rate: row.grn_avg_rate ?? 0,
            })),
        }));
        userEditedRef.current = true;
    };

    const materialGroups = useMemo(() => {
        let rowNo = 0;
        return groupMaterials(computedSheet.materials || []).map((group) => ({
            ...group,
            rows: group.rows.map((row) => {
                rowNo += 1;
                return { ...row, displayNo: rowNo };
            }),
        }));
    }, [computedSheet.materials]);

    const handleGradeFilterChange = useCallback((grade) => {
        setGradeFilter(grade);
        setSelectedMixId(null);
        setSelectedMixRow(null);
        userEditedRef.current = true;
        setSheet((prev) => calculateRateTotals({
            ...prev,
            materials: (prev.materials || []).map((row) => ({ ...row, quantity: 0, amount: 0 })),
            selected_design_mix_id: null,
            selected_design_mix_label: null,
            selected_design_mix_recipe: null,
        }));
    }, []);

    useEffect(() => {
        if (gradeFilter) return;
        const lineGrades = uniqueGradesFromLines(lines);
        if (lineGrades.length === 1) {
            setGradeFilter(lineGrades[0]);
        }
    }, [gradeFilter, lines]);

    const handleSelectDesignMix = useCallback((mixRow) => {
        if (!plantId || !mixRow?.id) return;

        const mixLabel = `${mixRow.grade || ''} · ${mixRow.site || mixRow.customer || ''}`.trim();
        const recipe = mixRecipeSnapshot({ ...mixRow, label: mixLabel });

        const applyMixToCurrentSheet = (baseSheet) => {
            const materials = applyDesignMixToMaterials(
                baseSheet.materials || [],
                mixRow,
                0,
            );
            return calculateRateTotals({
                ...baseSheet,
                materials,
                volume_m3: RATE_WORKING_BASIS_M3,
                selected_design_mix_id: mixRow.id,
                selected_design_mix_label: mixLabel,
                selected_design_mix_recipe: recipe,
            });
        };

        userEditedRef.current = true;
        setSelectedMixId(mixRow.id);
        setSelectedMixRow(mixRow);

        if (sheetRef.current.materials?.length) {
            applySheet(applyMixToCurrentSheet(sheetRef.current));
        }

        const syncFromServer = (baseSheet) => {
            setLoading(true);
            applyDesignMixToRateWorking(cookies, {
                plant_id: plantId,
                design_mix_id: mixRow.id,
                volume_m3: 0,
                cement_kg: totals.total_cement_kg,
                sand_kg: totals.total_sand_kg,
                aggregate_kg: totals.total_aggregate_kg,
                distance_round_trip_km: distanceRoundTripKm || 0,
                estimator_id: editId || undefined,
            })
                .then((data) => {
                    applySheet(calculateRateTotals({
                        ...data,
                        volume_m3: RATE_WORKING_BASIS_M3,
                        selected_design_mix_id: mixRow.id,
                        selected_design_mix_label: mixLabel,
                        selected_design_mix_recipe: recipe,
                    }));
                })
                .catch(() => {
                    if (!baseSheet?.materials?.length) {
                        Swal.fire('Error', 'Could not load materials for rate working.', 'error');
                        return;
                    }
                    Swal.fire(
                        'Recipe applied locally',
                        'Could not refresh rates from server; quantities were filled from the selected recipe.',
                        'warning',
                    );
                })
                .finally(() => setLoading(false));
        };

        if (!sheetRef.current.materials?.length) {
            setLoading(true);
            getRateWorkingPreview(cookies, {
                plant_id: plantId,
                cement_kg: totals.total_cement_kg,
                sand_kg: totals.total_sand_kg,
                aggregate_kg: totals.total_aggregate_kg,
                volume_m3: totals.total_volume_m3,
                distance_round_trip_km: distanceRoundTripKm || 0,
                estimator_id: editId || undefined,
            })
                .then((data) => {
                    const nextSheet = applyMixToCurrentSheet(data);
                    applySheet(nextSheet);
                    syncFromServer(nextSheet);
                })
                .catch(() => {
                    Swal.fire('Error', 'Could not load materials for rate working.', 'error');
                })
                .finally(() => setLoading(false));
            return;
        }

        syncFromServer(sheetRef.current);
    }, [
        applySheet,
        cookies,
        distanceRoundTripKm,
        editId,
        plantId,
        totals.total_aggregate_kg,
        totals.total_cement_kg,
        totals.total_sand_kg,
    ]);

    if (!plantId) {
        return (
            <div className="estimator-rate-panel mb-3">
                <h5 className="mb-2">Rate Working</h5>
                <p className="text-muted small mb-0">Select a plant to load raw materials and GRN average rates.</p>
            </div>
        );
    }

    return (
        <div className="estimator-rate-panel mb-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                <h5 className="mb-0">Rate Working</h5>
                <div>
                    <Button type="button" color="secondary" size="sm" className="mr-2" disabled={loading} onClick={() => loadPreview(false)}>
                        {loading ? 'Loading…' : 'Refresh materials'}
                    </Button>
                    <Button type="button" color="outline-secondary" size="sm" onClick={resetGrnRates}>
                        Reset rates to GRN avg
                    </Button>
                </div>
            </div>
            <p className="text-muted small">
                Quantities are per 1 m³. Select a design mix recipe to fill material qty from the mix.
                GRN rates use the last 2 months when available.
            </p>

            <DesignMixSelector
                cookies={cookies}
                plantId={plantId}
                searchText={designMixSearch}
                onSearchChange={setDesignMixSearch}
                gradeFilter={gradeFilter}
                onGradeFilterChange={handleGradeFilterChange}
                selectedMixId={selectedMixId || sheet.selected_design_mix_id}
                selectedMixLabel={sheet.selected_design_mix_label}
                selectedMixRecipe={sheet.selected_design_mix_recipe || selectedMixRow}
                onSelectMix={handleSelectDesignMix}
            />

            <div className="table-responsive">
                <Table bordered size="sm" className="estimator-rate-table mb-2">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Raw Material</th>
                            <th>Unit</th>
                            <th>Qty (kg/m³)</th>
                            <th>GRN Avg Rate</th>
                            <th>Rate</th>
                            <th className="text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {materialGroups.map((group) => {
                            const groupSubtotal = num(
                                computedSheet.material_group_subtotals?.[group.key],
                                group.rows.reduce((sum, row) => sum + num(row.amount), 0),
                            );
                            return (
                                <Fragment key={group.key}>
                                    <tr className="estimator-rate-group-header">
                                        <td colSpan={7}>{group.label}</td>
                                    </tr>
                                    {group.rows.map((row) => (
                                        <tr key={row.product_id || `${group.key}-${row.displayNo}`}>
                                            <td>{row.displayNo}</td>
                                            <td>{row.product_name}</td>
                                            <td>{formatRateWorkingUnit(row.unit)}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.001"
                                                    className="form-control form-control-sm estimator-rate-input"
                                                    value={row.quantity ?? ''}
                                                    onChange={(e) => updateMaterial(row.product_id, 'quantity', e.target.value)}
                                                />
                                            </td>
                                            <td className="text-right">{formatCurrency(row.grn_avg_rate)}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className="form-control form-control-sm estimator-rate-input"
                                                    value={row.rate ?? ''}
                                                    onChange={(e) => updateMaterial(row.product_id, 'rate', e.target.value)}
                                                />
                                            </td>
                                            <td className="text-right">{formatCurrency(row.amount)}</td>
                                        </tr>
                                    ))}
                                    <tr className="estimator-rate-group-subtotal">
                                        <td colSpan={6} className="text-right">{group.label} Subtotal</td>
                                        <td className="text-right">{formatCurrency(groupSubtotal)}</td>
                                    </tr>
                                </Fragment>
                            );
                        })}
                        {!materialGroups.length && (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">No raw materials found for this plant.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="row">
                <div className="col-lg-6">
                    <div className="estimator-rate-charges">
                        <div className="form-row align-items-center mb-2">
                            <label className="col-sm-5 col-form-label col-form-label-sm">Diesel Rate (/Ltr)</label>
                            <div className="col-sm-7">
                                <div className="d-flex">
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="form-control form-control-sm"
                                        value={sheet.diesel_rate ?? ''}
                                        onChange={(e) => updateField('diesel_rate', e.target.value)}
                                        placeholder="Auto from last month diesel GRN"
                                    />
                                    <Button
                                        type="button"
                                        color="secondary"
                                        size="sm"
                                        className="ml-1"
                                        style={{ whiteSpace: 'nowrap' }}
                                        disabled={!plantId}
                                        onClick={fetchGrnDieselRate}
                                    >
                                        GRN
                                    </Button>
                                </div>
                                <small className="text-muted d-block">
                                    Last month diesel GRN avg: {grnDieselRate > 0 ? formatCurrency(grnDieselRate) : '0.00'} / Ltr
                                </small>
                            </div>
                        </div>
                        <div className="form-row align-items-center mb-2">
                            <label className="col-sm-5 col-form-label col-form-label-sm">Vehicle Capacity (m³)</label>
                            <div className="col-sm-7">
                                <input
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    className="form-control form-control-sm"
                                    value={sheet.vehicle_capacity ?? DEFAULT_VEHICLE_CAPACITY_M3}
                                    onChange={(e) => updateField('vehicle_capacity', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="form-row align-items-center mb-2">
                            <label className="col-sm-5 col-form-label col-form-label-sm">Average KM/Ltr</label>
                            <div className="col-sm-7">
                                <input
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    className="form-control form-control-sm"
                                    value={sheet.avg_km_per_ltr ?? DEFAULT_AVG_KM_PER_LTR}
                                    onChange={(e) => updateField('avg_km_per_ltr', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="form-row align-items-center mb-2">
                            <label className="col-sm-5 col-form-label col-form-label-sm">Company Overhead</label>
                            <div className="col-sm-7">
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="form-control form-control-sm"
                                    value={sheet.company_overhead ?? ''}
                                    onChange={(e) => updateField('company_overhead', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="form-row align-items-center mb-2">
                            <label className="col-sm-5 col-form-label col-form-label-sm">Transport Charges</label>
                            <div className="col-sm-7">
                                <input
                                    type="text"
                                    readOnly
                                    className="form-control form-control-sm bg-light"
                                    value={formatCurrency(transportCharges)}
                                />
                                <small className="text-muted d-block">
                                    ({distanceRoundTripKm || 0} km ÷ {num(sheet.avg_km_per_ltr, DEFAULT_AVG_KM_PER_LTR)} km/L
                                    × {formatCurrency(sheet.diesel_rate)} ÷ {num(sheet.vehicle_capacity, DEFAULT_VEHICLE_CAPACITY_M3)} m³)
                                </small>
                            </div>
                        </div>
                        <div className="form-row align-items-center mb-2">
                            <label className="col-sm-5 col-form-label col-form-label-sm">Profit</label>
                            <div className="col-sm-7 d-flex">
                                <select
                                    className="form-control form-control-sm mr-1"
                                    style={{ maxWidth: 110 }}
                                    value={sheet.profit_mode || 'percent'}
                                    onChange={(e) => updateField('profit_mode', e.target.value)}
                                >
                                    <option value="percent">%</option>
                                    <option value="amount">Amount</option>
                                </select>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="form-control form-control-sm"
                                    value={sheet.profit_value ?? ''}
                                    onChange={(e) => updateField('profit_value', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="form-row align-items-center mb-2">
                            <label className="col-sm-5 col-form-label col-form-label-sm">Tax</label>
                            <div className="col-sm-7">
                                <select
                                    className="form-control form-control-sm"
                                    value={sheet.tax_id || ''}
                                    onChange={onTaxChange}
                                >
                                    <option value="">GST @ 18% (default)</option>
                                    {taxList.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <Table bordered size="sm" className="estimator-rate-summary mb-0">
                        <tbody>
                            <tr>
                                <td>Material Subtotal</td>
                                <td className="text-right">{formatCurrency(computedSheet.material_subtotal)}</td>
                            </tr>
                            <tr>
                                <td>Company Overhead</td>
                                <td className="text-right">{formatCurrency(computedSheet.company_overhead)}</td>
                            </tr>
                            <tr>
                                <td>Transport Charges</td>
                                <td className="text-right">{formatCurrency(computedSheet.transport_charges)}</td>
                            </tr>
                            <tr>
                                <td>Profit ({sheet.profit_mode === 'amount' ? 'Amount' : `${num(sheet.profit_value)}%`})</td>
                                <td className="text-right">{formatCurrency(computedSheet.profit_amount)}</td>
                            </tr>
                            <tr className="font-weight-bold estimator-rate-summary-gross">
                                <td>Gross Total (before tax)</td>
                                <td className="text-right">{formatCurrency(computedSheet.gross_total)}</td>
                            </tr>
                            <tr>
                                <td>{formatTaxLabel(computedSheet.tax_name, computedSheet.tax_percent)}</td>
                                <td className="text-right">{formatCurrency(computedSheet.tax_amount)}</td>
                            </tr>
                            <tr className="font-weight-bold estimator-rate-summary-net">
                                <td>Net Total</td>
                                <td className="text-right">{formatCurrency(computedSheet.net_total)}</td>
                            </tr>
                            <tr className="font-weight-bold estimator-rate-summary-rate">
                                <td>Calculated Rate / m³</td>
                                <td className="text-right">{formatRatePerM3(computedSheet.rate_per_m3)}</td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
}

export default ConcreteEstimatorRateWorking;
